import { useState, useEffect, useRef, MutableRefObject } from 'react';
import { IClaimDetails, IImportedCashlessClaims } from '../../../types';
import GreenTick from '../../../assets/green-tick.svg';
import ImportedClaimsCard from '../ImportedClaimCard';
import Button from 'ui/mobile-ds/Button';
import get from 'lodash.get';
import { Layout } from '@/components/Common/Layout';
import useClaimCreateStore, {
  EClaimCreateFlowSteps,
} from '@/store/claimCreateStore';
import clsx from 'clsx';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import AppConfig from '@/AppConfig';
import toast from 'react-hot-toast';
import {
  createInsurerClaim,
  importClaimsQuery,
} from '@/queries/claims.queries';
import useConfirm from '@/hooks/useConfirm';
import { getDimensions, track } from '@/utils';
import { TRACKER_EVENTS } from '@/constants';
import { IClaimCreateStepProps } from '@/pages/create';
import { useAppContext } from '@/context/AppContext';
import useMobileBackPress from '@/hooks/useMobileBackPress';

const POLLING_TIMEOUT = 20 * 1000; // 20 seconds

const ImportCashlessClaims = ({ trackCreation }: IClaimCreateStepProps) => {
  const navigate = useNavigate();
  const formState = useClaimCreateStore((state) => state.formState);

  const setCurrentStep = useClaimCreateStore((state) => state.setCurrentStep);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRedirectingToCreate, setIsRedirectingToCreate] =
    useState<boolean>(false);

  const firstFetchEpoch: MutableRefObject<number | null> = useRef(null);

  const {
    data: importedClaims,
    isFetched,
    refetch,
    fetchStatus: importedClaimsFetchStatus,
  } = useQuery<IImportedCashlessClaims>({
    ...importClaimsQuery(formState.patientId ?? ''),
  });

  const numberOfClaims = importedClaims?.results.length ?? 0;
  const [selectedClaim, setSelectedClaim] = useState<IClaimDetails | null>(
    numberOfClaims === 1 ? get(importedClaims, 'results[0]', null) : null,
  );

  const { triggerConfirm, Alert } = useConfirm();
  const { isWebView } = useAppContext();

  useMobileBackPress({
    onBackPress: () => {
      setCurrentStep(EClaimCreateFlowSteps.SELECT_PATIENT);
    },
    isWebView,
  });

  useEffect(() => {
    if (numberOfClaims === 1) {
      setSelectedClaim(get(importedClaims, 'results[0]', null));
    }
  }, [importedClaims, numberOfClaims]);

  const { mutate, status: insurerClaimCreationStatus } = useMutation({
    retry: false,
    mutationFn: ({
      insurerClaimId,
      patientId,
    }: {
      insurerClaimId: string;
      patientId: string;
    }) => {
      return createInsurerClaim(
        insurerClaimId ?? '',
        patientId ?? '',
        isWebView ? 'Plum WebView' : 'Plum Web App',
      ).mutationFn({ insurerClaimId, patientId });
    },
    mutationKey: createInsurerClaim(
      selectedClaim?.insurerClaimId ?? '',
      selectedClaim?.patientId ?? '',
      isWebView ? 'Plum WebView' : 'Plum Web App',
    ).mutationKey,
  });

  useEffect(() => {
    if (importedClaimsFetchStatus === 'fetching' && !firstFetchEpoch.current) {
      // if the first fetch epoch is null, set it to the current time
      if (firstFetchEpoch.current == null) {
        firstFetchEpoch.current = Date.now();
      }
    }
  }, [importedClaimsFetchStatus]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    if (importedClaimsFetchStatus === 'idle') {
      if (importedClaims?.status === 'inProgress') {
        timeoutId = setTimeout(() => {
          window.dev.i('Polling for imported claims', {
            firstFetchEpoch: firstFetchEpoch.current,
            timeElapsed: Date.now() - (firstFetchEpoch.current ?? 0),
          });
          // if the time elapsed since the first fetch is less than the polling timeout
          // then continue polling
          if (
            firstFetchEpoch.current &&
            Date.now() - firstFetchEpoch.current < POLLING_TIMEOUT
          ) {
            refetch();
          } else {
            // if the time elapsed since the first fetch is greater than the polling timeout
            // exit polling and redirect
            setIsRedirectingToCreate(true);
            setTimeout(() => {
              setCurrentStep(EClaimCreateFlowSteps.COLLECT_BASIC_INFO);
            }, 2500);
          }
        }, 5000);
      } else {
        if (
          (importedClaims?.results?.length ?? 0) === 0 ||
          importedClaims?.status === 'failed'
        ) {
          setIsRedirectingToCreate(true);
          setTimeout(() => {
            setCurrentStep(EClaimCreateFlowSteps.COLLECT_BASIC_INFO);
          }, 2500);
        } else {
          setIsLoading(false);
          track({
            event: TRACKER_EVENTS.CC_CLAIM_IMPORT_VIEWED,
            data: {
              type:
                importedClaims?.claimsDataSource === 'insurer'
                  ? 'import'
                  : 'redirect',
            },
          });
        }
      }
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    isFetched,
    importedClaims,
    setCurrentStep,
    refetch,
    importedClaimsFetchStatus,
  ]);

  const redirectToStatusPage = (plumClaimId: string) => {
    navigate(`${AppConfig.BASE_PATH}/claims/${plumClaimId}`);
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (
    event,
  ) => {
    event.preventDefault();
    if (selectedClaim) {
      if (importedClaims?.claimsDataSource === 'insurer') {
        mutate(
          {
            insurerClaimId: selectedClaim?.insurerClaimId ?? '',
            patientId: selectedClaim?.patientId ?? '',
          },
          {
            onSuccess: (data) => {
              trackCreation?.(data, {
                creationMode:
                  (importedClaims?.results.length ?? 0) > 1
                    ? 'import-from-multiple'
                    : 'import-single',
              });
              redirectToStatusPage(data.plumClaimId);
            },
          },
        );
      } else {
        const plumClaimId = selectedClaim?.plumClaimId;
        if (plumClaimId) {
          track({
            event: TRACKER_EVENTS.CC_CLAIM_IMPORT_REDIRECT,
          });
          redirectToStatusPage(plumClaimId);
        }
      }
    } else {
      toast.error('You need to select a claim to continue', {
        id: 'create-claim-error',
        style: {
          marginBottom: getDimensions('claims-footer').height,
        },
      });
    }
  };

  const isPlumClaim = importedClaims?.claimsDataSource === 'plum';

  return (
    <Layout.Main
      loading={isLoading}
      loaderProps={{
        title: isRedirectingToCreate
          ? 'Initialising a new claim request'
          : "We're trying to find any already ongoing claims",
        description: 'This will just take a moment',
      }}
      fullWidth
      headerProps={{
        hideBack: isFetched,
        onBack: () => setCurrentStep(EClaimCreateFlowSteps.SELECT_PATIENT),
      }}
    >
      {({ getDimensions }) => {
        const footerDimensions = getDimensions('claims-footer');
        return (
          <div
            className="flex flex-col flex-1 h-full"
            style={{
              paddingBottom: footerDimensions.height,
            }}
          >
            <div
              className={clsx({
                'flex flex-col items-center px-6 md:mt-[52px] lg:mt-0 py-1':
                  true,
                '!mt-[84px]': isWebView,
              })}
            >
              <img
                src={GreenTick}
                alt="green checkmark, claims found"
                height={56}
                width={56}
              />
              <div className="mt-5 text-center">
                <h1 className="text-mobile-text-main font-chromatica font-black leading-[140%] text-xl md:text-2xl">
                  {isPlumClaim
                    ? numberOfClaims > 1
                      ? `You have ${numberOfClaims} ongoing claim requests for this patient`
                      : 'You have an ongoing claim request for this patient'
                    : numberOfClaims > 1
                      ? `We found ${numberOfClaims} ongoing insurer claims for this patient`
                      : 'We found an ongoing insurer claim for this patient'}
                </h1>
                <p className="mt-2 text-sm md:text-base font-medium text-mobile-text-hierarchy-1 leading-[140%]">
                  {isPlumClaim
                    ? 'Avoid losing context by selecting an ongoing claim'
                    : 'Select the claim you need help with to get started'}
                </p>
              </div>
            </div>
            <form
              className="pb-24 flex flex-col flex-1"
              onSubmit={handleSubmit}
            >
              <div
                className={clsx({
                  'mt-12 grid grid-cols-1 md:grid-cols-2 h-full w-full md:w-auto flex-1 mx-auto gap-5 md:gap-8':
                    true,
                  '!grid-cols-1': importedClaims?.results.length === 1,
                })}
              >
                {importedClaims?.results.map((claim, _, arr) => {
                  return (
                    <ImportedClaimsCard
                      key={claim.plumClaimId || claim.insurerClaimId}
                      totalClaims={arr.length}
                      name="imported-claim"
                      claimDetails={claim}
                      source={importedClaims.claimsDataSource}
                      isSelectable={numberOfClaims > 1}
                      value={
                        isPlumClaim ? claim.plumClaimId : claim.insurerClaimId
                      }
                      isSelected={
                        selectedClaim
                          ? selectedClaim.plumClaimId === claim.plumClaimId &&
                            selectedClaim.insurerClaimId ===
                              claim.insurerClaimId
                          : false
                      }
                      onChange={() => {
                        setSelectedClaim(claim);
                      }}
                    />
                  );
                })}
              </div>
              <Layout.Footer
                isSticky
                containerClassName="flex justify-end gap-5"
              >
                <Button
                  onClick={async () => {
                    // if it's a plum claim or the number of insurer claims is just one
                    // then don't show the dialog
                    let isConfirmed = isPlumClaim || numberOfClaims === 1;
                    if (isConfirmed === false) {
                      isConfirmed = await triggerConfirm({
                        title: 'Cancel claim request?',
                        description:
                          'If you leave this page, your claim request will not be created',
                        cancelButtonText: 'Stay on this page',
                        confirmButtonText: 'Cancel',
                      });
                    }
                    if (isConfirmed) {
                      setCurrentStep(EClaimCreateFlowSteps.SELECT_PATIENT);
                    }
                  }}
                  type="button"
                  intent="secondary"
                  className="ml-auto md:max-w-[260px] w-full md:text-base border-[1px] border-primary-500 bg-transparent"
                >
                  Go back
                </Button>
                <Button
                  loading={insurerClaimCreationStatus === 'pending'}
                  type="submit"
                  className="md:max-w-[260px] w-full md:text-base"
                >
                  {selectedClaim
                    ? isPlumClaim
                      ? 'Take me there'
                      : 'Proceed'
                    : 'Select a claim'}
                </Button>
              </Layout.Footer>
            </form>
            <Alert />
          </div>
        );
      }}
    </Layout.Main>
  );
};

export default ImportCashlessClaims;
